/**
 * Audio director (AD-861, docs/architecture/client-audio.md §5–§6): turns what the client already
 * receives into cues. Two inputs, no new server traffic:
 *
 * 1. The feedback bus (`gameFeedback`, shared with the VFX pass): hits, damage, Dodge, abilities,
 *    gather / craft / loot / quest / level-up, deaths, respawns → SFX via `cueForFeedback`.
 * 2. The raw GameEvents Game.onEvent sees, for what the bus does not carry: the OWN swing (the
 *    `attack` echo), enemy-specific telegraph cues by name (Sentinel, Ruin Shade), and the music
 *    state (yard → combat → boss → victory → yard).
 *
 * Presentation only; never changes game state.
 */
import type { GameEvent } from '@great-cloud/shared/client';
import type { GameFeedbackEvent } from '../feedback/feedbackBus.js';
import type { CueMap, MusicState, ResolveContext } from './cueMap.js';
import { cueForEnemy, cueForFeedback, isBossName } from './cueMap.js';
import type { VoiceMap } from './voice.js';

export interface CueSink {
  play(cue: string): unknown;
  setMusicState(state: MusicState): void;
  /** Speak a voice-bus cue (an enemy bark); optional so SFX-only sinks stay valid. */
  speak?(cue: string, onEnd: () => void): unknown;
}

/** A bark was spoken: who, and the exact words (the subtitle). */
export type BarkListener = (speaker: string, text: string) => void;

/** No own combat for this long ⇒ the music returns to the yard. */
export const COMBAT_DECAY_MS = 12_000;
/** The victory cue holds this long before the yard returns (a missing track is silent). */
export const VICTORY_HOLD_MS = 10_000;

export class AudioDirector {
  private lastCombatMs = -Infinity;
  private bossId: number | null = null;
  private bossName: string | undefined;
  private voice: VoiceMap | null = null;
  private onBark: BarkListener | null = null;
  private victoryUntilMs = 0;
  private state: MusicState = 'yard';
  private ctx: ResolveContext = {};

  constructor(
    private readonly map: CueMap,
    private readonly sink: CueSink,
    private readonly now: () => number,
  ) {}

  get musicState(): MusicState {
    return this.state;
  }

  /** The spoken-line map (enemy barks); null = no voice. */
  setVoice(voice: VoiceMap | null, onBark: BarkListener | null = null): void {
    this.voice = voice;
    this.onBark = onBark;
  }

  /** An enemy bark (aggro / phase / defeat) by display name, if one is voiced. */
  private bark(name: string | undefined, which: 'aggro' | 'phase' | 'defeat'): void {
    const cue = name ? this.voice?.enemies[name]?.[which] : undefined;
    if (!cue || !this.voice || !this.sink.speak) return;
    this.sink.speak(cue, () => undefined);
    this.onBark?.(this.voice.speakers[cue] ?? name ?? '', this.voice.texts[cue] ?? '');
  }

  setResolveContext(ctx: ResolveContext): void {
    this.ctx = ctx;
  }

  /** Entering a zone resets the music to the yard state. */
  enterZone(): void {
    this.bossId = null;
    this.victoryUntilMs = 0;
    this.lastCombatMs = -Infinity;
    this.go('yard');
  }

  onFeedback(ev: GameFeedbackEvent): void {
    const cue = cueForFeedback(this.map, ev, this.ctx);
    if (cue) this.sink.play(cue);
    if (ev.kind === 'playerDamaged' || ev.kind === 'weaponHit') this.combat();
  }

  /**
   * Raw GameEvent tap (one line in Game.onEvent). `nameOf` resolves an entity's display name.
   */
  noteGameEvent(event: GameEvent, ownId: number, nameOf: (id: number) => string | undefined): void {
    switch (event.type) {
      case 'attack':
        if (event.sourceId === ownId) {
          this.sink.play('combat.swing');
          this.combat();
        } else if (event.targetId === ownId) this.combat();
        return;
      case 'abilityUsed':
        if (event.sourceId === ownId && event.targetId !== undefined) this.combat();
        return;
      case 'telegraph': {
        const name = nameOf(event.sourceId);
        const cue = cueForEnemy(this.map, name, 'telegraph');
        if (cue) this.sink.play(cue);
        if (isBossName(this.map, name)) {
          // First telegraph of an engagement = the boss has noticed the player: its aggro bark.
          if (this.bossId !== event.sourceId) this.bark(name, 'aggro');
          this.bossId = event.sourceId;
          this.bossName = name;
          this.go('boss');
        }
        this.combat();
        return;
      }
      case 'telegraphResolve': {
        const cue = cueForEnemy(this.map, nameOf(event.sourceId), 'land');
        if (cue) this.sink.play(cue);
        return;
      }
      case 'entityDied':
        if (this.bossId !== null && event.entityId === this.bossId) {
          this.bark(this.bossName, 'defeat');
          this.bossId = null;
          this.victoryUntilMs = this.now() + VICTORY_HOLD_MS;
          this.go('victory');
        }
        return;
      default:
        return;
    }
  }

  /** Per-frame (or timer) tick: decays combat / victory back to the yard. */
  tick(): void {
    const t = this.now();
    if (this.state === 'victory' && t >= this.victoryUntilMs) this.go('yard');
    else if (
      (this.state === 'combat' || this.state === 'boss') &&
      t - this.lastCombatMs >= COMBAT_DECAY_MS
    )
      this.go('yard'); // a boss walked away from also returns to the yard
  }

  private combat(): void {
    this.lastCombatMs = this.now();
    if (this.state === 'yard') this.go('combat');
  }

  private go(state: MusicState): void {
    if (this.state === state && state !== 'yard') return;
    this.state = state;
    this.sink.setMusicState(state);
  }
}
